import { getDatabase, ref, set, update, onValue } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";

class KycDocumentModel {
    constructor(userId, documentType, file = null) {
        this.userId = userId;
        this.documentType = documentType; // 'id_front', 'id_back', 'proof_address', 'selfie'
        this.file = file;
        this.timestamp = new Date().toISOString();
    }

    /**
     * Sube un documento a Firebase Storage y guarda la referencia en Database
     * @returns {Promise} Promesa con el resultado de la operación
     */
    async uploadDocument() {
        return new Promise((resolve, reject) => {
            if (!this.file) {
                reject(new Error("No file selected"));
                return;
            }

            // Validar tamaño del archivo (máximo 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (this.file.size > maxSize) {
                reject(new Error("File size exceeds 5MB limit"));
                return;
            }

            // Validar tipo de archivo
            const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
            if (!validTypes.includes(this.file.type)) {
                reject(new Error("Invalid file type. Only JPG, PNG and PDF are allowed"));
                return;
            }

            const storage = getStorage();
            const fileName = `${this.documentType}_${this.timestamp}`;
            const fileRef = storageRef(storage, `kyc_documents/${this.userId}/${fileName}`);
            const uploadTask = uploadBytesResumable(fileRef, this.file);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    console.log(`Upload is ${progress}% done`);
                },
                (error) => {
                    console.error("Error uploading document:", error);
                    reject(error);
                },
                async () => {
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        
                        // Guardar referencia en Database
                        const db = getDatabase();
                        const documentRef = ref(db, `kyc_documents/${this.userId}/${this.documentType}`);
                        
                        const documentData = {
                            documentType: this.documentType,
                            status: 'pending', // pending, approved, rejected
                            downloadURL: downloadURL,
                            fileName: fileName,
                            uploadDate: this.timestamp,
                            reviewDate: null,
                            reviewNotes: '',
                            fileType: this.file.type,
                            fileSize: this.file.size
                        };

                        await set(documentRef, documentData);
                        resolve({
                            success: true,
                            documentData: documentData
                        });
                    } catch (error) {
                        reject(error);
                    }
                }
            );
        });
    }

    /**
     * Obtiene todos los documentos KYC de un usuario
     * @param {string} userId - ID del usuario
     * @param {Function} callback - Función callback para recibir los datos
     */
    static getUserDocuments(userId, callback) {
        const db = getDatabase();
        const documentsRef = ref(db, `kyc_documents/${userId}`);
        
        return onValue(documentsRef, (snapshot) => {
            const data = snapshot.val();
            callback(data || {});
        });
    }

    /**
     * Actualiza el estado de un documento (solo para administradores)
     * @param {string} userId - ID del usuario
     * @param {string} documentType - Tipo de documento
     * @param {string} status - Nuevo estado (approved, rejected)
     * @param {string} notes - Notas de revisión
     * @returns {Promise}
     */
    static async updateDocumentStatus(userId, documentType, status, notes = '') {
        const db = getDatabase();
        const documentRef = ref(db, `kyc_documents/${userId}/${documentType}`);
        
        const updates = {
            status: status,
            reviewDate: new Date().toISOString(),
            reviewNotes: notes
        };

        return update(documentRef, updates);
    }

    /**
     * Elimina un documento (tanto de Storage como de Database)
     * @param {string} userId - ID del usuario
     * @param {string} documentType - Tipo de documento
     * @param {string} fileName - Nombre del archivo en Storage
     * @returns {Promise}
     */
    static async deleteDocument(userId, documentType, fileName) {
        try {
            // Eliminar de Storage
            const storage = getStorage();
            const fileRef = storageRef(storage, `kyc_documents/${userId}/${fileName}`);
            await deleteObject(fileRef);

            // Eliminar de Database
            const db = getDatabase();
            const documentRef = ref(db, `kyc_documents/${userId}/${documentType}`);
            await set(documentRef, null);

            return { success: true };
        } catch (error) {
            console.error("Error deleting document:", error);
            throw error;
        }
    }

    /**
     * Verifica si el usuario tiene todos los documentos aprobados
     * @param {string} userId - ID del usuario
     * @returns {Promise<boolean>}
     */
    static async isKycComplete(userId) {
        return new Promise((resolve) => {
            const db = getDatabase();
            const documentsRef = ref(db, `kyc_documents/${userId}`);
            
            onValue(documentsRef, (snapshot) => {
                const data = snapshot.val();
                if (!data) {
                    resolve(false);
                    return;
                }

                const requiredDocuments = ['id_front', 'id_back', 'proof_address', 'selfie'];
                const allApproved = requiredDocuments.every(docType => 
                    data[docType] && data[docType].status === 'approved'
                );

                resolve(allApproved);
            }, { onlyOnce: true });
        });
    }
}

export default KycDocumentModel;
